import { normalizeTitle, validTitle } from './title-model';

export interface DatabaseTask {
  id: number;
  title: string;
  completed: boolean;
}
// The same rows the lesson tables use: identities with gaps, not list positions.
export const databaseTasks = (): DatabaseTask[] => [
  { id: 1, title: 'Prepare the demo', completed: false },
  { id: 4, title: "Read Maya's notes", completed: true },
  { id: 8, title: 'Check the examples', completed: false },
];
const copyRows = (rows: DatabaseTask[]) => rows.map((row) => ({ ...row }));

export type WriterDesign = 'copies' | 'local-locks' | 'coordinator';
export function writersFrame(step: number, design: WriterDesign) {
  const original: DatabaseTask[] = [
    { id: 1, title: 'Prepare the demo', completed: false },
    { id: 2, title: 'Read the feedback', completed: false },
  ];
  const a = step >= 1 ? copyRows(original) : null;
  const b = step >= 2 && design !== 'coordinator' ? copyRows(original) : null;
  let stored = copyRows(original);
  if (step >= 3) {
    if (a?.[0]) a[0].completed = true;
    stored = copyRows(a ?? original);
  }
  const latestB = step >= 4 && design === 'coordinator' ? copyRows(stored) : b;
  if (step >= 4 && latestB?.[1]) {
    latestB[1].title = 'Read the new feedback';
    stored = copyRows(latestB);
  }
  return { a, b: latestB, stored, lostUpdate: step >= 4 && design !== 'coordinator' };
}

export type QueryFilter = 'all' | 'open' | 'complete';
export type QueryProjection = 'rows' | 'titles';
export type QueryOrder = 'asc' | 'desc';
export function queryRows(rows: DatabaseTask[], filter: QueryFilter, order: QueryOrder) {
  return rows
    .filter((row) => filter === 'all' || row.completed === (filter === 'complete'))
    .map((row) => ({ ...row }))
    .sort((a, b) => (order === 'asc' ? a.id - b.id : b.id - a.id));
}
export function queryTemplate(filter: QueryFilter, projection: QueryProjection, order: QueryOrder) {
  return `SELECT ${projection === 'rows' ? 'id, title, completed' : 'title'}\nFROM tasks${filter === 'all' ? '' : `\nWHERE completed = ${filter === 'complete' ? 'TRUE' : 'FALSE'}`}\nORDER BY id ${order.toUpperCase()};`;
}

export type SchemaExample = 'valid' | 'duplicate' | 'boolean' | 'blank' | 'nul';
export function schemaCandidate(example: SchemaExample) {
  return {
    id: example === 'duplicate' ? 1 : 7,
    title:
      example === 'blank' ? '   ' : example === 'nul' ? 'Review\u0000notes' : "Review Maya's notes",
    completed: example === 'boolean' ? 'perhaps' : false,
  };
}
export function inspectDatabaseRow(
  existing: DatabaseTask[],
  candidate: { id: number; title: string; completed: string | boolean },
): { accepted: false; reason: string } | { accepted: true; row: DatabaseTask; reason: string } {
  if (!Number.isSafeInteger(candidate.id) || candidate.id < 1)
    return { accepted: false, reason: 'The task ID must be a positive integer.' };
  if (existing.some((row) => row.id === candidate.id))
    return { accepted: false, reason: `Primary key conflict: ID ${candidate.id} already exists.` };
  if (candidate.completed === 'perhaps')
    return { accepted: false, reason: '“perhaps” is not a valid PostgreSQL boolean input.' };
  if (typeof candidate.completed !== 'boolean')
    return { accepted: false, reason: 'This example expects a typed boolean value.' };
  if (candidate.title.includes('\u0000'))
    return { accepted: false, reason: 'PostgreSQL text cannot contain a NUL character.' };
  if (!validTitle(candidate.title) || normalizeTitle(candidate.title) !== candidate.title)
    return {
      accepted: false,
      reason:
        'The title constraint requires 1–80 characters with surrounding spaces, tabs and line breaks already removed.',
    };
  return {
    accepted: true,
    row: { ...candidate, completed: candidate.completed },
    reason: 'All column and row rules passed. One row was inserted.',
  };
}

export interface ConnectionState {
  app: boolean;
  database: boolean;
  connected: boolean;
  pending: DatabaseTask[] | null;
  stored: DatabaseTask[];
  message: string;
}
export const initialConnection = (): ConnectionState => ({
  app: true,
  database: true,
  connected: false,
  pending: null,
  stored: databaseTasks(),
  message: 'Both processes are running. The application has no database connection yet.',
});
export type ConnectionAction =
  | 'connect'
  | 'begin'
  | 'change'
  | 'commit'
  | 'disconnect'
  | 'stop-app'
  | 'start-app'
  | 'stop-database'
  | 'start-database'
  | 'reset';
export function connectionTransition(
  state: ConnectionState,
  action: ConnectionAction,
): ConnectionState {
  switch (action) {
    case 'reset':
      return initialConnection();
    case 'connect':
      return state.app && state.database && !state.connected
        ? {
            ...state,
            connected: true,
            message:
              'The application opened a connection. No transaction has begun in this explicit-BEGIN example.',
          }
        : state;
    case 'begin':
      return state.connected && !state.pending
        ? {
            ...state,
            pending: copyRows(state.stored),
            message:
              'A transaction began on this connection. Changes will stay pending until commit.',
          }
        : state;
    case 'change':
      return state.pending
        ? {
            ...state,
            pending: state.pending.map((row) => (row.id === 1 ? { ...row, completed: true } : row)),
            message: 'Task #1 is complete inside this transaction. Committed data is unchanged.',
          }
        : state;
    case 'commit':
      return state.pending
        ? {
            ...state,
            stored: copyRows(state.pending),
            pending: null,
            message:
              'Commit accepted the transaction. The connection remains open for another transaction.',
          }
        : state;
    case 'disconnect':
      return state.connected
        ? {
            ...state,
            connected: false,
            pending: null,
            message: state.pending
              ? 'Disconnect discarded the uncommitted transaction. Both processes and committed data remain.'
              : 'The connection closed. Both processes and committed data remain.',
          }
        : state;
    case 'stop-app':
      return {
        ...state,
        app: false,
        connected: false,
        pending: null,
        message:
          'The application stopped. Its connection closed and pending work was discarded; committed database data remains.',
      };
    case 'start-app':
      return {
        ...state,
        app: true,
        message: 'The application started. It still needs to open a new connection.',
      };
    case 'stop-database':
      return {
        ...state,
        database: false,
        connected: false,
        pending: null,
        message:
          'The database process stopped. Connections closed and pending work was discarded. Committed data remains on storage.',
      };
    case 'start-database':
      return {
        ...state,
        database: true,
        message:
          'The database process restarted with its committed data. The application must reconnect.',
      };
  }
}

export type ScopeOperation = 'update' | 'delete';
export type ScopeTarget = '1' | '99' | 'all';
export function scopeChange(rows: DatabaseTask[], operation: ScopeOperation, target: ScopeTarget) {
  const matches = (row: DatabaseTask) => target === 'all' || row.id === Number(target);
  const matchedIds = rows.filter(matches).map((row) => row.id);
  const after =
    operation === 'delete'
      ? rows.filter((row) => !matches(row)).map((row) => ({ ...row }))
      : rows.map((row) => (matches(row) ? { ...row, completed: true } : { ...row }));
  const sql = `${operation === 'update' ? 'UPDATE tasks\nSET completed = TRUE' : 'DELETE FROM tasks'}${target === 'all' ? '' : `\nWHERE id = ${target}`}\nRETURNING id, title, completed;`;
  return { after, matchedIds, sql };
}

export type ParameterDesign = 'interpolation' | 'bound';
export function parameterFrame(title: string, design: ParameterDesign) {
  const bound = design === 'bound';
  return {
    template: bound
      ? 'INSERT INTO tasks (title)\nVALUES (%s) RETURNING id, title, completed;'
      : `INSERT INTO tasks (title)\nVALUES ('${title}') RETURNING id, title, completed;`,
    parameters: bound ? JSON.stringify([title]) : 'No separate parameter value',
    hasQuote: title.includes("'"),
    message: bound
      ? 'The SQL template stays fixed. The driver sends the title separately as one value, including any apostrophe.'
      : title.includes("'")
        ? 'The title entered the SQL text. Apostrophes are now part of SQL syntax: interpolation can break the statement or change its meaning.'
        : 'This title happens to fit the quoted literal. Add an apostrophe to challenge the assumption.',
  };
}

export interface TransactionState {
  phase: 'idle' | 'active' | 'aborted';
  stored: DatabaseTask[];
  pending: DatabaseTask[] | null;
  message: string;
}
export const initialTransaction = (): TransactionState => ({
  phase: 'idle',
  stored: databaseTasks(),
  pending: null,
  message: 'No transaction is open. Begin before changing either task.',
});
export type TransactionAction =
  'begin' | 'complete' | 'rename' | 'error' | 'commit' | 'rollback' | 'reset';
export function transactionTransition(
  state: TransactionState,
  action: TransactionAction,
): TransactionState {
  if (action === 'reset') return initialTransaction();
  if (action === 'begin')
    return state.phase === 'idle'
      ? {
          ...state,
          phase: 'active',
          pending: copyRows(state.stored),
          message:
            'BEGIN opened a transaction. Its own reads can include pending changes; other connections cannot read those changes yet.',
        }
      : state;
  if (action === 'rollback')
    return state.phase !== 'idle'
      ? {
          ...state,
          phase: 'idle',
          pending: null,
          message:
            'ROLLBACK ended the transaction and discarded all its changes. Committed rows stayed unchanged.',
        }
      : state;
  if (state.phase === 'aborted' && action === 'commit')
    return {
      ...state,
      phase: 'idle',
      pending: null,
      message:
        'COMMIT on an aborted PostgreSQL transaction ends it with ROLLBACK. None of its proposed changes became committed.',
    };
  if (state.phase === 'aborted')
    return {
      ...state,
      message:
        'The transaction is aborted. Further statements cannot succeed; roll back before starting another transaction.',
    };
  if (state.phase !== 'active' || !state.pending) return state;
  if (action === 'error')
    return {
      ...state,
      phase: 'aborted',
      message:
        'A blank-title constraint failed. The entire transaction is now aborted. Earlier pending changes cannot be committed.',
    };
  if (action === 'commit')
    return {
      ...state,
      phase: 'idle',
      stored: copyRows(state.pending),
      pending: null,
      message:
        'COMMIT accepted all pending changes together. A new read on another connection now sees the committed rows.',
    };
  if (action === 'complete')
    return {
      ...state,
      pending: state.pending.map((row) => (row.id === 1 ? { ...row, completed: true } : row)),
      message:
        'Task #1 changed inside this transaction. A new read on another connection still sees the old committed row.',
    };
  return {
    ...state,
    pending: state.pending.map((row) => (row.id === 4 ? { ...row, title: 'Review draft' } : row)),
    message:
      'Task #4 changed inside the same transaction. Neither pending change is committed yet.',
  };
}

export type ImportCounterRule = 'survivors' | 'historical';
export interface IdentityState {
  rows: DatabaseTask[];
  nextId: number;
  issued: number[];
  message: string;
}
export const historicalTasks = (): DatabaseTask[] => [
  { id: 1, title: 'Prepare the demo', completed: false },
  { id: 4, title: "Read Maya's notes", completed: true },
];
export const initialIdentity = (): IdentityState => ({
  rows: [],
  nextId: 1,
  issued: [],
  message: 'The table is empty and the unused identity sequence starts at 1.',
});
export type IdentityAction = 'insert' | 'delete' | 'rollback' | 'import' | 'reset';
export function identityTransition(
  state: IdentityState,
  action: IdentityAction,
  rule: ImportCounterRule,
): IdentityState {
  switch (action) {
    case 'reset':
      return initialIdentity();
    case 'insert':
      return {
        rows: [...state.rows, { id: state.nextId, title: 'New task', completed: false }],
        nextId: state.nextId + 1,
        issued: [...state.issued, state.nextId],
        message: `Created task #${state.nextId}. The sequence advanced to ${state.nextId + 1}.`,
      };
    case 'delete': {
      const last = state.rows.at(-1);
      return last
        ? {
            ...state,
            rows: state.rows.filter((row) => row.id !== last.id),
            message: `Deleted task #${last.id}. Deletion does not rewind the identity sequence; the next generated ID is still ${state.nextId}.`,
          }
        : state;
    }
    case 'rollback':
      return {
        ...state,
        nextId: state.nextId + 1,
        issued: [...state.issued, state.nextId],
        message: `The insert generated ID ${state.nextId}, then rolled back. No row was kept. The sequence keeps advancing even after rollback; the next ID is ${state.nextId + 1}.`,
      };
    case 'import':
      return state.rows.length === 0 && state.nextId === 1
        ? {
            rows: historicalTasks(),
            nextId: rule === 'historical' ? 8 : 5,
            issued: [],
            message:
              rule === 'historical'
                ? 'Imported the remaining tasks with IDs 1 and 4, and restarted the identity sequence at JSON next_id = 8 in the import transaction. Deleted IDs 5–7 will not be reused.'
                : 'Imported the remaining tasks with IDs 1 and 4, then used MAX(id) + 1 = 5. This forgot the JSON counter: IDs 5–7 were previously issued and deleted.',
          }
        : {
            ...state,
            message:
              'Import requires an empty table and an unused ID sequence. Reset before comparing import rules.',
          };
  }
}
